import React from 'react';
import { RoutingAction, ActionType, RedirectAction, RandomizeAction, ImageAction } from '../../../types/qr.types';
import { RedirectActionEditor } from './actions/RedirectActionEditor';
import { RandomizeActionEditor } from './actions/RandomizeActionEditor';
import { ImageActionEditor } from './actions/ImageActionEditor';

interface RuleActionEditorProps {
  action: RoutingAction;
  onChange: (action: RoutingAction) => void;
}

export function RuleActionEditor({ action, onChange }: RuleActionEditorProps) {
  const handleTypeChange = (type: ActionType) => {
    if (type === action.type) return;

    if (type === 'redirect') {
      onChange({ type: 'redirect', url: '' });
    } else if (type === 'randomize') {
      onChange({
        type: 'randomize',
        urls: [
          { url: '', weight: 1 },
          { url: '', weight: 1 },
        ],
      });
    } else {
      onChange({ type: 'image', image_id: 0 });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Action</h4>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => handleTypeChange('redirect')}
          className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
            action.type === 'redirect'
              ? 'border-[--clinqr-navy] bg-[--clinqr-lime]'
              : 'border-[--clinqr-border]/10 hover:border-[--clinqr-border]/20'
          }`}
        >
          <div className="flex items-center gap-2">
            <svg className={`w-5 h-5 ${action.type === 'redirect' ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-muted]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span className={`font-medium ${action.type === 'redirect' ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-secondary]'}`}>
              Redirect
            </span>
          </div>
          <p className={`text-sm mt-1 ${action.type === 'redirect' ? 'text-[--clinqr-navy]/70' : 'text-[--clinqr-text-muted]'}`}>
            Send to a single URL
          </p>
        </button>

        <button
          type="button"
          onClick={() => handleTypeChange('randomize')}
          className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
            action.type === 'randomize'
              ? 'border-[--clinqr-navy] bg-[--clinqr-lime]'
              : 'border-[--clinqr-border]/10 hover:border-[--clinqr-border]/20'
          }`}
        >
          <div className="flex items-center gap-2">
            <svg className={`w-5 h-5 ${action.type === 'randomize' ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-muted]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className={`font-medium ${action.type === 'randomize' ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-secondary]'}`}>
              A/B Test
            </span>
          </div>
          <p className={`text-sm mt-1 ${action.type === 'randomize' ? 'text-[--clinqr-navy]/70' : 'text-[--clinqr-text-muted]'}`}>
            Split traffic between URLs
          </p>
        </button>

        <button
          type="button"
          onClick={() => handleTypeChange('image')}
          className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
            action.type === 'image'
              ? 'border-[--clinqr-navy] bg-[--clinqr-lime]'
              : 'border-[--clinqr-border]/10 hover:border-[--clinqr-border]/20'
          }`}
        >
          <div className="flex items-center gap-2">
            <svg className={`w-5 h-5 ${action.type === 'image' ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-muted]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className={`font-medium ${action.type === 'image' ? 'text-[--clinqr-navy]' : 'text-[--clinqr-text-secondary]'}`}>
              Image
            </span>
          </div>
          <p className={`text-sm mt-1 ${action.type === 'image' ? 'text-[--clinqr-navy]/70' : 'text-[--clinqr-text-muted]'}`}>
            Show an uploaded image
          </p>
        </button>
      </div>

      <div className="pt-2">
        {action.type === 'redirect' ? (
          <RedirectActionEditor
            action={action as RedirectAction}
            onChange={onChange}
          />
        ) : action.type === 'randomize' ? (
          <RandomizeActionEditor
            action={action as RandomizeAction}
            onChange={onChange}
          />
        ) : (
          <ImageActionEditor
            action={action as ImageAction}
            onChange={onChange}
          />
        )}
      </div>
    </div>
  );
}
